import { memo, useMemo } from 'react'

import { CurrentTimeOfDay } from '@/components/rundown/current-time-of-day'

import { cn } from '@/utils'
import { addMilliseconds } from 'date-fns'
import { useAtomValue } from 'jotai'

import type { Rundown } from '~shared/types'

import { momentAtom } from '@/store/moment.store.js'
import { rundownAtom } from '@/store/rundown.store.js'
import { runnerAtom } from '@/store/runner.store.js'

import { RundownOutputTextFitWrapper } from '@/features/rundown-output/RundownOutputTextFitWrapper.js'
import { useCuesById } from '@/features/rundown/data/cues.store'

import { getTimestampByCueIdAtom } from '../../store/timestamps.store.js'
import formatTimeOfDay from '../../utils/formatTimeOfDay.js'

import * as OUTPUT_TYPES from './rundown-output.const.ts'
import { RundownOutputActiveCueTimer } from './RundownOutputSubTimers'
import { useRundownOutputOptions } from './useRundownOutputOptions.ts'

export function RundownOutputLayoutSingle() {
  const rundown = useAtomValue(rundownAtom)
  const moment = useAtomValue(momentAtom)
  const runner = useAtomValue(runnerAtom)
  const cues = useCuesById()

  const { getOption } = useRundownOutputOptions()

  if (!rundown) return null

  // `moment` is null whenever the rundown is not running. The layout still
  // renders its structure so the output does not go blank between shows: the
  // timer falls back to `--:--` and the cue-dependent blocks stay empty. This
  // matches the header, which renders the same way when nothing is running.
  const currentCue =
    moment?.cueId && runner?.timesnap?.cueId ? cues[moment.cueId] : null

  const nextCue = runner?.nextCueId ? cues[runner.nextCueId] : null

  const outputMessage = (
    rundown.settings as Rundown['settings'] & {
      outputMessage?: {
        text: string
        visible: boolean
        color?: string
        bold?: boolean
        underline?: boolean
      }
    }
  ).outputMessage ?? {
    text: '',
    visible: false,
    color: '#ffffff',
    bold: false,
    underline: false,
  }

  return (
    <div className="overflow-hidden">
      {/* FIRST */}
      <div style={{ height: '18vh' }}>
        {(getOption(OUTPUT_TYPES.START_TIMES) === OUTPUT_TYPES.ON ||
          getOption(OUTPUT_TYPES.END_TIMES) === OUTPUT_TYPES.ON) && (
          <RundownOutputTextFitWrapper height="5vh">
            <StartEndTimeDisplay
              currentCueId={currentCue?.id}
              timezone={rundown?.timezone}
              todDisplayFormat={rundown?.settings?.todDisplayFormat}
            />
          </RundownOutputTextFitWrapper>
        )}

        {getOption(OUTPUT_TYPES.LARGE_TIME_OF_DAY) === OUTPUT_TYPES.ON && (
          <RundownOutputTextFitWrapper height="13vh">
            <LargeTimeOfDay
              className="opacity-60"
              timezone={rundown?.timezone}
              todDisplayFormat={rundown.settings?.todDisplayFormat}
            />
          </RundownOutputTextFitWrapper>
        )}
      </div>

      {/* SECOND */}
      <div style={{ height: '40vh' }}>
        <RundownOutputActiveCueTimer
          layout="stack"
          heightVh={40}
        />
      </div>

      {/* THIRD */}
      <div
        style={{ height: '16vh' }}
        className="px-5 md:px-10"
      >
        {outputMessage.visible && outputMessage.text ? (
          <div style={{ color: outputMessage.color || '#ffffff' }}>
            <RundownOutputTextFitWrapper
              height="16vh"
              multiLine
            >
              <span
                className={cn(
                  outputMessage.bold && 'font-bold',
                  outputMessage.underline && 'underline',
                )}
              >
                {outputMessage.text}
              </span>
            </RundownOutputTextFitWrapper>
          </div>
        ) : (
          <div style={{ height: '10vh' }}>
            <RundownOutputTextFitWrapper
              height="7vh"
              multiLine
            >
              {currentCue?.title}
            </RundownOutputTextFitWrapper>
            <RundownOutputTextFitWrapper
              height="4vh"
              multiLine
            >
              {currentCue?.subtitle}
            </RundownOutputTextFitWrapper>
          </div>
        )}
      </div>

      {/* FORTH */}
      <div style={{ height: '14vh' }}>
        {getOption(OUTPUT_TYPES.NEXT_CUE) === OUTPUT_TYPES.ON && (
          <RundownOutputTextFitWrapper
            height="4vh"
            className="opacity-60"
          >
            {nextCue && (
              <div
                className="flex items-start gap-[3svw]"
                style={{ height: '4vh' }}
              >
                <div className="bg-white text-black px-[0.5vh] font-semibold">
                  NEXT
                </div>
                <div>{nextCue?.title}</div>
              </div>
            )}
          </RundownOutputTextFitWrapper>
        )}
      </div>
    </div>
  )
}

function LargeTimeOfDay({
  className,
  timezone,
  todDisplayFormat,
}: {
  className: string
  timezone: string
  todDisplayFormat: Rundown['settings']['todDisplayFormat']
}) {
  return (
    <div className={cn('font-mono font-semibold whitespace-nowrap', className)}>
      <CurrentTimeOfDay
        timezone={timezone}
        todDisplayFormat={todDisplayFormat}
      />
    </div>
  )
}

/**
 * Shows formatted start and end times for the current cue.
 *
 * Memoized to prevent re-renders unless relevant props change
 * (currentCueId, optionStartTimes, optionEndTimes, timezone),
 * optimizing performance when parent component re-renders frequently.
 */
const StartEndTimeDisplay = memo(
  ({
    currentCueId,
    timezone = 'UTC',
    todDisplayFormat,
  }: {
    currentCueId?: string
    timezone?: string
    todDisplayFormat: Rundown['settings']['todDisplayFormat']
  }) => {
    const getTimestampByCueId = useAtomValue(getTimestampByCueIdAtom)

    const { getOption } = useRundownOutputOptions()

    const optionStartTimes = getOption(OUTPUT_TYPES.START_TIMES)
    const optionEndTimes = getOption(OUTPUT_TYPES.END_TIMES)

    const { startTimeText, endTimeText } = useMemo(() => {
      const timestamp = getTimestampByCueId(currentCueId)
      if (!timestamp) return {}
      const startTimeText =
        optionStartTimes === OUTPUT_TYPES.ON
          ? formatTimeOfDay(timestamp.actual.start, {
              timezone,
              format: todDisplayFormat,
            })
          : ''
      const endTimeText =
        optionEndTimes === OUTPUT_TYPES.ON
          ? formatTimeOfDay(
              addMilliseconds(
                timestamp.actual.start,
                timestamp.actual.duration,
              ),
              {
                timezone,
                format: todDisplayFormat,
              },
            )
          : ''

      return { startTimeText, endTimeText }
    }, [
      optionEndTimes,
      optionStartTimes,
      currentCueId,
      getTimestampByCueId,
      timezone,
      todDisplayFormat,
    ])

    if (!startTimeText && !endTimeText) return null

    return (
      <div className="w-full flex items-center justify-center gap-6 font-mono leading-none">
        {startTimeText && <p>{startTimeText}</p>}
        {startTimeText && endTimeText && <p>→</p>}
        {endTimeText && <p>{endTimeText}</p>}
      </div>
    )
  },
)

StartEndTimeDisplay.displayName = 'StartEndTimeDisplay'
